import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ProcessDesign } from './process-design';
export { ProcessDesign };
import { processDesignSchema } from '@schemas/business-objects/process-design';
import { ProcessDesignActivityCountFormArray } from './process-design-activity-count-form-group';
import { ProcessDesignHistoryFormArray } from './process-design-history-form-group';
import { ProcessInstanceFormArray } from './process-instance-form-group';
import { ProcessInstanceSummaryFormArray } from './process-instance-summary-form-group';
import { TenantFormGroup } from './tenant-form-group';
import type {
  ProcessAppEvent,
  ProcessDataType,
  ProcessFunction,
  ProcessInfo,
  ProcessService,
  ProcessUserInterface,
  RoleInfo,
} from '../interface-definitions/index';
import type { Id } from '../base-types';
import type { ActivityMapping } from './activity-mapping';
import type { LatestProcessDesign } from './latest-process-design';
import type { ProcessMessage } from './process-message';
import type { ProcessRole } from './process-role';
import type { Token } from './token';
import { ProcessDataTypeFormGroup } from './process-data-type-form-group';
import { ProcessFunctionFormGroup } from './process-function-form-group';
import { ProcessServiceFormGroup } from './process-service-form-group';
import { ProcessUserInterfaceFormGroup } from './process-user-interface-form-group';

export class ProcessDesignFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    designUuid: SchemaFormControl;
    name: SchemaFormControl;
    displayName: SchemaFormControl;
    description: SchemaFormControl;
    version: SchemaFormControl;
    suspended: SchemaFormControl;
    sha1Sum: SchemaFormControl;
    designJson: SchemaFormControl;
    analysis: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    processInstances: ProcessInstanceFormArray;
    processDesignHistories: ProcessDesignHistoryFormArray;
    processDesignActivityCounts: ProcessDesignActivityCountFormArray;
    processInstanceSummaries: ProcessInstanceSummaryFormArray;
    tenantId: SchemaFormControl;
    tenant: TenantFormGroup;
  };

  declare value: Partial<ProcessDesign>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(processDesignSchema, ProcessDesign, data, options);
  }

  get object(): ProcessDesign {
    return new ProcessDesign(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'processInstances':
        return new ProcessInstanceFormArray();
      case 'processDesignHistories':
        return new ProcessDesignHistoryFormArray();
      case 'processDesignActivityCounts':
        return new ProcessDesignActivityCountFormArray();
      case 'processInstanceSummaries':
        return new ProcessInstanceSummaryFormArray();
      case 'tenant':
        return new TenantFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ProcessDesign[]> {
    return ProcessDesign.find(filter);
  }

  static async findOne(filter?: any): Promise<ProcessDesign | null> {
    return ProcessDesign.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<ProcessDesign> {
    return ProcessDesign.findById(id, filter);
  }

  static async create(data: Partial<ProcessDesign>): Promise<ProcessDesign> {
    return ProcessDesign.create(data as any);
  }

  static async updateById(
    id: string,
    updates: Partial<ProcessDesign>,
  ): Promise<ProcessDesign> {
    return ProcessDesign.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return ProcessDesign.deleteById(id);
  }

  async getMigrationCandidateCount(): Promise<number> {
    const instance = new ProcessDesign(this.getRawValue());
    return instance.getMigrationCandidateCount();
  }

  async getMigrationOverview(): Promise<any> {
    const instance = new ProcessDesign(this.getRawValue());
    return instance.getMigrationOverview();
  }

  async migrateReadyInstances(options: any): Promise<any> {
    const instance = new ProcessDesign(this.getRawValue());
    return instance.migrateReadyInstances(options);
  }

  async activate(): Promise<any> {
    const instance = new ProcessDesign(this.getRawValue());
    return instance.activate();
  }

  static async getAppEvents(): Promise<ProcessAppEvent[]> {
    return ProcessDesign.getAppEvents();
  }

  static async getDataTypes(): Promise<ProcessDataTypeFormGroup[]> {
    const result = await ProcessDesign.getDataTypes();
    return (result || []).map((item: any) => {
      const fg = new ProcessDataTypeFormGroup();
      fg._populate(item);
      return fg;
    });
  }

  static async getFunctions(): Promise<ProcessFunctionFormGroup[]> {
    const result = await ProcessDesign.getFunctions();
    return (result || []).map((item: any) => {
      const fg = new ProcessFunctionFormGroup();
      fg._populate(item);
      return fg;
    });
  }

  static async getProcesses(): Promise<ProcessInfo[]> {
    return ProcessDesign.getProcesses();
  }

  static async getRoles(): Promise<RoleInfo[]> {
    return ProcessDesign.getRoles();
  }

  static async getServices(): Promise<ProcessServiceFormGroup[]> {
    const result = await ProcessDesign.getServices();
    return (result || []).map((item: any) => {
      const fg = new ProcessServiceFormGroup();
      fg._populate(item);
      return fg;
    });
  }

  static async getUserInterfaces(): Promise<ProcessUserInterfaceFormGroup[]> {
    const result = await ProcessDesign.getUserInterfaces();
    return (result || []).map((item: any) => {
      const fg = new ProcessUserInterfaceFormGroup();
      fg._populate(item);
      return fg;
    });
  }

  static async loadFromFiles(): Promise<
    Array<{ name: string; version: number; status: string }>
  > {
    return ProcessDesign.loadFromFiles();
  }

  static async loadFromGenerated(): Promise<
    Array<{ name: string; version: number; status: string }>
  > {
    return ProcessDesign.loadFromGenerated();
  }

  static async reanalyzeAll(): Promise<any> {
    return ProcessDesign.reanalyzeAll();
  }

  static async reanalyzeOutdated(): Promise<any> {
    return ProcessDesign.reanalyzeOutdated();
  }

  async reanalyze(): Promise<any> {
    const instance = new ProcessDesign(this.getRawValue());
    return instance.reanalyze();
  }

  async suspend(): Promise<any> {
    const instance = new ProcessDesign(this.getRawValue());
    return instance.suspend();
  }

  static async upload(options: any): Promise<any> {
    return ProcessDesign.upload(options);
  }
}

export class ProcessDesignFormArray extends PersistedFormArray {
  declare controls: ProcessDesignFormGroup[];

  readonly entityName = 'ProcessDesign' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(processDesignSchema, ProcessDesign, options);
  }

  protected override createItemGroup() {
    return new ProcessDesignFormGroup();
  }

  get array(): ProcessDesign[] {
    return this.controls.map((group: ProcessDesignFormGroup) => group.object);
  }
}

export class ProcessDesignPersistedArray extends PersistedArray<ProcessDesign> {
  readonly entityName = 'ProcessDesign' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ProcessDesign, options);
  }
}
